import { lazy } from 'react';
import {
  createBrowserRouter,
  redirect,
  useNavigate,
  LoaderFunctionArgs,
} from 'react-router-dom';
import { ErrorPage, Loader, usePageLoaderStore } from '@tasama/ui';

// Layout shells — always loaded (thin wrappers, no code-splitting benefit)
import { Layout } from '../../components/Layout';
import { AppLayout } from '../../components/AppLayout';
import { AccountLayout } from '../../components/AccountLayout';
import { CompanyEditLayout } from '../../shared/components/CompanyEditLayout';

// Non-component imports
import { PORTAL_ROUTES } from '@tasama/config';
import { AppErrorPage } from '../pages';
import { accountManagementService } from '../../services/account-management-service';
import {
  hasAdminAccess,
  userContextService,
} from '../../services/user-context.service';
import { useAuthStore } from '../../stores/auth-store';
import { apiClient, queryClient } from '../../config/api-config';
import { getServiceDetailsQueryKey } from '../../features/my-services/hooks/useServiceDetailsQuery';
import { useServiceDetailsStore } from '../../features/my-services/stores';
import type { ServiceDetailsResponse } from '../../features/my-services/types';
import { BUNDLE_STATE } from '../../features/my-services/types/bundle-state.constants';
import { normalizeServiceStatus } from '../../shared/utils/status';
import {
  ndaLoaderWithRefresh,
  kybLoaderWithRefresh,
  companyEditLoaderWithRefresh,
  msaLoaderWithRefresh,
} from './loader-helpers';

// Route-level page components — lazy-loaded for code splitting
const LoginPage = lazy(() =>
  import('../../pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('../../pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const Activate = lazy(() =>
  import('../../pages/account').then((m) => ({ default: m.Activate }))
);
const RequestReactivation = lazy(() =>
  import('../../pages/account').then((m) => ({
    default: m.RequestReactivation,
  }))
);
const ResetPassword = lazy(() =>
  import('../../pages/account').then((m) => ({ default: m.ResetPassword }))
);
const RequestResetPassword = lazy(() =>
  import('../../pages/account').then((m) => ({
    default: m.RequestResetPassword,
  }))
);
const HomePage = lazy(() =>
  import('../../features/home').then((m) => ({ default: m.HomePage }))
);
const ServiceCataloguePage = lazy(() =>
  import('../../features/ServiceCatalogue').then((m) => ({
    default: m.ServiceCataloguePage,
  }))
);
const ServiceCatalogueL2Page = lazy(() =>
  import('../../features/ServiceCatalogue').then((m) => ({
    default: m.ServiceCatalogueL2Page,
  }))
);
const MyServicesPage = lazy(() =>
  import('../../features/my-services/pages').then((m) => ({
    default: m.MyServicesPage,
  }))
);
const MyAccountPage = lazy(() =>
  import('../../features/my-account').then((m) => ({
    default: m.MyAccountPage,
  }))
);
const NotificationsPage = lazy(() =>
  import('../../features/Notifications').then((m) => ({
    default: m.NotificationsPage,
  }))
);
const MyTasksPage = lazy(() =>
  import('../../features/my-tasks').then((m) => ({
    default: m.MyTasksPage,
  }))
);
const DashboardsPage = lazy(() =>
  import('../../features/dashboards').then((m) => ({
    default: m.DashboardsPage,
  }))
);
const CatalogSyncPage = lazy(() =>
  import('../../features/super-admin').then((m) => ({
    default: m.CatalogSyncPage,
  }))
);
const SelectCompanyPage = lazy(() =>
  import('../../features/super-admin').then((m) => ({
    default: m.SelectCompanyPage,
  }))
);
const SupportTicketsPage = lazy(() =>
  import('../../pages').then((m) => ({ default: m.SupportTicketsPage }))
);
const MSALayout = lazy(() =>
  import('../../features/msa/pages/MSALayout').then((m) => ({
    default: m.MSALayout,
  }))
);
const MSAInitiateLayout = lazy(() =>
  import('../../features/msa/pages/InitiateMSA/MSAInitiateLayout').then(
    (m) => ({
      default: m.MSAInitiateLayout,
    })
  )
);
const MSAReviewLayout = lazy(() =>
  import('../../features/msa/pages/ReviewMSA/MSAReviewLayout').then((m) => ({
    default: m.MSAReviewLayout,
  }))
);
const NDALayout = lazy(() =>
  import('../../features/nda').then((m) => ({ default: m.NDALayout }))
);
const AppInfoPage = lazy(() =>
  import('../../pages').then((m) => ({ default: m.AppInfoPage }))
);
const ServiceDetailPage = lazy(() =>
  import('../../features/my-services/pages').then((m) => ({
    default: m.ServiceDetailPage,
  }))
);
const BundleDetailPage = lazy(() =>
  import('../../features/my-services/pages').then((m) => ({
    default: m.BundleDetailPage,
  }))
);
const SowPageLayout = lazy(() =>
  import('../../features/my-services/components').then((m) => ({
    default: m.SowPageLayout,
  }))
);
const BundleSowPage = lazy(() =>
  import('../../features/ServiceCatalogue').then((m) => ({
    default: m.BundleSowPage,
  }))
);
const ActivityRequestPageLayout = lazy(() =>
  import('../../features/my-services/components').then((m) => ({
    default: m.ActivityRequestPageLayout,
  }))
);
const ServiceContractLayout = lazy(() =>
  import('../../features/my-services/pages/service-contract').then((m) => ({
    default: m.ServiceContractLayout,
  }))
);
const ProposalReviewLayout = lazy(() =>
  import('../../features/my-services/pages/proposal-review').then((m) => ({
    default: m.ProposalReviewLayout,
  }))
);
const POLayout = lazy(() =>
  import('../../features/my-services/pages/purchase-order').then((m) => ({
    default: m.POLayout,
  }))
);
const NotFoundWrapper = () => {
  const navigate = useNavigate();
  return (
    <ErrorPage
      variant="not-found"
      testID="portal-404"
      onHomeClick={() => navigate(PORTAL_ROUTES.HOME)}
      onBackClick={() => navigate(-1)}
    />
  );
};

const DocumentAttestationLayout = lazy(() =>
  import('@/components/DocumentAttestationLayout').then((m) => ({
    default: m.DocumentAttestationLayout,
  }))
);
const KickoffDocumentationLayout = lazy(() =>
  import('@/components/KickoffDocumentationLayout').then((m) => ({
    default: m.KickoffDocumentationLayout,
  }))
);

const ProofOfPaymentPage = lazy(() =>
  import('@/features/my-services/business-landing/components/proof-of-payment/ProofOfPaymentPage').then(
    (m) => ({ default: m.default })
  )
);

// Loader to validate token query parameter
const validateTokenLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const token = url.searchParams.get('token');

  if (!token) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  // Ensure auth state is resolved before checking — no-op if AuthProvider has
  // already completed checkAuthStatus() this session.
  await useAuthStore.getState().checkAuthStatus();

  // If a user is already authenticated, do NOT call the token validation API.
  // The existing session's cookies would be sent with the request, causing the
  // backend to treat the token as consumed before the new user can use it.
  // Signal the page to show a session-conflict prompt instead.
  if (useAuthStore.getState().isAuthenticated) {
    return { sessionConflict: true as const, token };
  }

  try {
    // Pass current path to help infer token type on 401 errors
    const response = await accountManagementService.validateToken(
      token,
      url.pathname
    );

    // If token is invalid, redirect based on type with reason in URL
    // Forward the original token and email so the re-request page can pre-fill and re-submit
    if (!response.valid) {
      const params = new URLSearchParams({ reason: 'invalid_link', token });
      if (response.email) params.set('email', response.email);
      if (response.type === 'reset' || response.type === 'forgot') {
        return redirect(
          `${PORTAL_ROUTES.ACCOUNT_REQUEST_RESET_PASSWORD}?${params}`
        );
      }
      if (response.type === 'activation') {
        return redirect(
          `${PORTAL_ROUTES.ACCOUNT_REQUEST_REACTIVATION}?${params}`
        );
      }
    }

    // Token is valid, allow access to the page
    return { token, email: response.email };
  } catch {
    // On API error (non-401), redirect to login
    return redirect(PORTAL_ROUTES.HOME);
  }
};

// Auth guard for the AppLayout parent route — runs once on initial load.
const homeLoader = async () => {
  await useAuthStore.getState().checkAuthStatus();

  if (!useAuthStore.getState().isAuthenticated) {
    return null;
  }

  // TDF-1389 AC5: a Super Admin belongs to no company, so no portal page is
  // reachable until one is chosen. The picker is shown instead.
  const context = (await userContextService.getUserContext()).data;
  if (context?.isSuperAdmin === true && !context.actingCompany) {
    return redirect(PORTAL_ROUTES.SELECT_COMPANY);
  }

  return null;
};

/**
 * TDF-1389: the picker itself. Only a Super Admin may see it, and one who has
 * already chosen is sent on to the portal rather than being asked again.
 */
const selectCompanyLoader = async () => {
  await useAuthStore.getState().checkAuthStatus();

  if (!useAuthStore.getState().isAuthenticated) {
    return redirect(PORTAL_ROUTES.LOGIN);
  }

  const context = (await userContextService.getUserContext()).data;
  if (context?.isSuperAdmin !== true) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  return null;
};

// MSA loader wrapper to extract pathname and call shared helper
const msaLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  return msaLoaderWithRefresh(url.pathname, url.pathname + url.search);
};

// Proposal-review / signed-contract / PO loaders (TDF-428/430/432, extended
// TDF-1342 Phase 3) — shared by Service Request and Bundle Request, switched
// on the `?serviceType=service|bundle` query param (absent means `service`,
// for backward compatibility with any pre-existing link). Each fetches the
// right details source and validates against the right allowed-states list
// for that type; the bundle path has no service-details-store bridge to
// populate, and proactively clears it so a stale service session can't leak
// into a bundle screen.
const PO_ALLOWED_STATES: Record<'service' | 'bundle', string[]> = {
  service: ['sr.state.po_required', 'sr.state.po_rejected'],
  bundle: [BUNDLE_STATE.PO_REQUIRED, BUNDLE_STATE.PO_REJECTED],
};
// rejectionReason/rejectedStep are never cleared on recovery (contract note,
// 2026-09-07) — only show the rejection banner when `state` itself is still
// the rejected one, not merely because `rejectedStep` matches.
const PO_REJECTED_STATE: Record<'service' | 'bundle', string> = {
  service: 'sr.state.po_rejected',
  bundle: BUNDLE_STATE.PO_REJECTED,
};

export const purchaseOrderLoader = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const cachedData = userContextService.getCachedUserContext();

  if (!hasAdminAccess(cachedData)) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  const { toAttemptServiceType } =
    await import('../../features/my-services/types/attempt-service-type');
  const serviceType = toAttemptServiceType(
    new URL(request.url).searchParams.get('serviceType')
  );
  const isBundle = serviceType === 'bundle';
  const attemptId = params.attemptId;
  if (!attemptId) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  try {
    usePageLoaderStore.getState().show();

    const { usePOUploadStore } =
      await import('../../features/my-services/stores/po-upload.store');

    const fetchedDetails = isBundle
      ? await (
          await import('../../features/my-services/services/bundle-details.service')
        ).bundleDetailsService.getBundleDetails(attemptId)
      : await (
          await import('../../features/my-services/services/service-details.service')
        ).serviceDetailsService.getServiceDetails(attemptId);

    if (!PO_ALLOWED_STATES[serviceType].includes(fetchedDetails?.state ?? '')) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    const { useServiceDetailsStore } =
      await import('../../features/my-services/stores/service-details.store');
    useServiceDetailsStore
      .getState()
      .setServiceDetails(
        isBundle ? null : (fetchedDetails as ServiceDetailsResponse)
      );

    usePOUploadStore.getState().setServiceType(serviceType);
    usePOUploadStore
      .getState()
      .setRejectionReason(
        fetchedDetails?.state === PO_REJECTED_STATE[serviceType] &&
          fetchedDetails?.rejectedStep === 'po'
          ? (fetchedDetails?.rejectionReason ?? null)
          : null
      );

    usePageLoaderStore.getState().hide();
    return null;
  } catch {
    usePageLoaderStore.getState().hide();
    return redirect(PORTAL_ROUTES.HOME);
  }
};

const CONTRACT_ALLOWED_STATES: Record<'service' | 'bundle', string[]> = {
  service: ['sr.state.contract_received', 'sr.state.contract_rejected'],
  bundle: [BUNDLE_STATE.CONTRACT_RECEIVED, BUNDLE_STATE.CONTRACT_REJECTED],
};
const CONTRACT_REJECTED_STATE: Record<'service' | 'bundle', string> = {
  service: 'sr.state.contract_rejected',
  bundle: BUNDLE_STATE.CONTRACT_REJECTED,
};

export const reviewContractLoader = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const cachedData = userContextService.getCachedUserContext();

  if (!hasAdminAccess(cachedData)) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  const { toAttemptServiceType } =
    await import('../../features/my-services/types/attempt-service-type');
  const serviceType = toAttemptServiceType(
    new URL(request.url).searchParams.get('serviceType')
  );
  const isBundle = serviceType === 'bundle';
  const attemptId = params.attemptId;
  if (!attemptId) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  try {
    usePageLoaderStore.getState().show();

    const { useServiceDetailsStore } =
      await import('../../features/my-services/stores/service-details.store');
    const { useSCUploadStore } =
      await import('../../features/my-services/stores/sc-upload.store');

    // Fetch fresh details so the component mounts with contractDocumentId ready
    const fetchedDetails = isBundle
      ? await (
          await import('../../features/my-services/services/bundle-details.service')
        ).bundleDetailsService.getBundleDetails(attemptId)
      : await (
          await import('../../features/my-services/services/service-details.service')
        ).serviceDetailsService.getServiceDetails(attemptId);

    if (
      !CONTRACT_ALLOWED_STATES[serviceType].includes(
        fetchedDetails?.state ?? ''
      )
    ) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    // Bundle has no service-details-store bridge (ServiceContractLayout
    // fetches its own contractDocumentId via useBundleDetailsQuery) — clear
    // it so a stale service session can't leak into the bundle screen.
    useServiceDetailsStore
      .getState()
      .setServiceDetails(
        isBundle ? null : (fetchedDetails as ServiceDetailsResponse)
      );

    useSCUploadStore.getState().setServiceType(serviceType);
    useSCUploadStore
      .getState()
      .setRejectionReason(
        fetchedDetails?.state === CONTRACT_REJECTED_STATE[serviceType] &&
          fetchedDetails?.rejectedStep === 'contract'
          ? (fetchedDetails?.rejectionReason ?? null)
          : null
      );

    // Leave loader visible — SCReviewPage's useEffect owns hide() after signed URL resolves
    return null;
  } catch {
    usePageLoaderStore.getState().hide();
    return redirect(PORTAL_ROUTES.HOME);
  }
};

const PROPOSAL_ALLOWED_STATES: Record<'service' | 'bundle', string[]> = {
  service: ['sr.state.proposal_received'],
  bundle: [BUNDLE_STATE.PROPOSAL_RECEIVED, BUNDLE_STATE.PROPOSAL_REJECTED],
};

export const proposalReviewLoader = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  const cachedData = userContextService.getCachedUserContext();

  // Only admin users can access the proposal review page (TDF-1393: an
  // acting Super Admin counts as admin here, see hasAdminAccess).
  if (!hasAdminAccess(cachedData)) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  const { toAttemptServiceType } =
    await import('../../features/my-services/types/attempt-service-type');
  const serviceType = toAttemptServiceType(
    new URL(request.url).searchParams.get('serviceType')
  );
  const isBundle = serviceType === 'bundle';
  const attemptId = params.attemptId;
  if (!attemptId) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  try {
    usePageLoaderStore.getState().show();
    const { useServiceDetailsStore } =
      await import('../../features/my-services/stores/service-details.store');

    const fetchedDetails = isBundle
      ? await (
          await import('../../features/my-services/services/bundle-details.service')
        ).bundleDetailsService.getBundleDetails(attemptId)
      : await (
          await import('../../features/my-services/services/service-details.service')
        ).serviceDetailsService.getServiceDetails(attemptId);

    if (
      !PROPOSAL_ALLOWED_STATES[serviceType].includes(
        fetchedDetails?.state ?? ''
      )
    ) {
      usePageLoaderStore.getState().hide();
      return redirect(PORTAL_ROUTES.HOME);
    }

    // Bundle has no service-details-store bridge — ProposalReviewLayout
    // fetches its own proposalDocumentId via useBundleDetailsQuery instead.
    useServiceDetailsStore
      .getState()
      .setServiceDetails(
        isBundle ? null : (fetchedDetails as ServiceDetailsResponse)
      );

    usePageLoaderStore.getState().hide();
    return null;
  } catch {
    // On error, hide loader and redirect to home
    usePageLoaderStore.getState().hide();
    return redirect(PORTAL_ROUTES.HOME);
  }
};

export const activityRequestLoader = async ({ params }: LoaderFunctionArgs) => {
  const { srAttemptId } = params;

  if (!srAttemptId || srAttemptId.trim() === '') {
    return redirect(PORTAL_ROUTES.HOME);
  }

  // Activity requests are only valid against an ACTIVE parent service (TDF-1450).
  // Redirect to the service detail page for any other state instead of letting
  // the form render for a parent that can't accept an activity yet.
  const redirectToServiceDetails = (status: string) =>
    redirect(
      `${PORTAL_ROUTES.SERVICES}/${normalizeServiceStatus(status).toLowerCase()}/${srAttemptId}/details`
    );

  const cached = queryClient.getQueryData<ServiceDetailsResponse>(
    getServiceDetailsQueryKey(srAttemptId)
  );

  if (cached?.srAttemptId) {
    if (normalizeServiceStatus(cached.status) !== 'ACTIVE') {
      return redirectToServiceDetails(cached.status);
    }
    useServiceDetailsStore.getState().setServiceDetails(cached);
    return null;
  }

  try {
    const { data } = await apiClient.axios.get<ServiceDetailsResponse>(
      `/services/service-request/${srAttemptId}/details`
    );

    if (normalizeServiceStatus(data.status) !== 'ACTIVE') {
      return redirectToServiceDetails(data.status);
    }

    queryClient.setQueryData(getServiceDetailsQueryKey(srAttemptId), data);
    useServiceDetailsStore.getState().setServiceDetails(data);
  } catch {
    // The parent's state is unknown when this fetch fails, so we can't confirm
    // it's ACTIVE. The backend does not gate activity submission on parent
    // state (TDF-1450), so fail closed here rather than letting the form
    // render against an unverified parent.
    return redirect(PORTAL_ROUTES.HOME);
  }

  return null;
};

/**
 * TDF-1461: the Super Admin catalog page is reachable only by a Super Admin who
 * also holds the catalog-publish grant. This keeps the URL from being a way past
 * the hidden menu item; the BFF and customer-service enforce it again server side.
 */
const superAdminCatalogLoader = () => {
  const cachedData = userContextService.getCachedUserContext();

  if (cachedData?.isSuperAdmin !== true) {
    return redirect(PORTAL_ROUTES.HOME);
  }
  if (cachedData?.canPublishCatalog !== true) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  return null;
};

export const sowLoader = ({ request }: LoaderFunctionArgs) => {
  const cachedData = userContextService.getCachedUserContext();

  // Only admin users can access the SOW page (TDF-1393: an acting Super
  // Admin counts as admin here, see hasAdminAccess).
  if (!hasAdminAccess(cachedData)) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  // Validate query parameters
  const url = new URL(request.url);
  const serviceId = url.searchParams.get('serviceId');
  const sourceSysId = url.searchParams.get('sourceSysId');

  // Redirect to home if required query params are missing
  if (
    !serviceId ||
    !sourceSysId ||
    serviceId.trim() === '' ||
    sourceSysId.trim() === ''
  ) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  return null;
};

export const bundleSowLoader = () => {
  const cachedData = userContextService.getCachedUserContext();

  // Only admin users can access the Bundle SOW page (mirrors sowLoader)
  if (!hasAdminAccess(cachedData)) {
    return redirect(PORTAL_ROUTES.HOME);
  }

  // The bundle itself is handed off via router state (set by BundleModal's
  // REQUEST BUNDLE button), not a query param — loaders can't see location
  // state, so a missing bundle is checked and redirected inside the page.
  return null;
};

export const router = createBrowserRouter(
  [
    {
      // Pathless root route — acts as a global error boundary for the entire app.
      // React Router intercepts errors before showing its built-in crash screen;
      // without errorElement here, that raw screen appears even in production.
      errorElement: <AppErrorPage />,
      HydrateFallback: () => <Loader className="pt-32" />,
      children: [
        {
          // TDF-1389: its own shell, no sidebar and no portal navigation.
          path: PORTAL_ROUTES.SELECT_COMPANY,
          element: <SelectCompanyPage />,
          loader: selectCompanyLoader,
        },
        {
          path: PORTAL_ROUTES.LOGIN,
          element: <Layout />,
          children: [
            {
              index: true,
              element: <LoginPage />,
            },
            {
              path: PORTAL_ROUTES.FORGOT_PASSWORD,
              element: <ForgotPasswordPage />,
            },
          ],
        },
        {
          path: PORTAL_ROUTES.ACCOUNT,
          element: <AccountLayout />,
          children: [
            {
              path: PORTAL_ROUTES.ACCOUNT_ACTIVATE,
              element: <Activate />,
              loader: validateTokenLoader,
            },
            {
              path: PORTAL_ROUTES.ACCOUNT_REQUEST_REACTIVATION,
              element: <RequestReactivation />,
            },
            {
              path: PORTAL_ROUTES.ACCOUNT_RESET_PASSWORD,
              element: <ResetPassword />,
              loader: validateTokenLoader,
            },
            {
              path: PORTAL_ROUTES.ACCOUNT_REQUEST_RESET_PASSWORD,
              element: <RequestResetPassword />,
            },
          ],
        },
        {
          path: PORTAL_ROUTES.HOME,
          element: <AppLayout />,
          loader: homeLoader,
          children: [
            {
              index: true,
              element: <HomePage />,
            },
            {
              path: PORTAL_ROUTES.SERVICE_CATALOGUE,
              element: <ServiceCataloguePage />,
            },
            {
              path: `${PORTAL_ROUTES.SERVICE_CATALOGUE}/:categoryId/:functionId`,
              element: <ServiceCatalogueL2Page />,
            },
            {
              path: PORTAL_ROUTES.SERVICES,
              element: <MyServicesPage />,
            },
            {
              path: `${PORTAL_ROUTES.SERVICES}/:serviceStatus/:srAttemptId/details`,
              element: <ServiceDetailPage />,
            },
            {
              path: `${PORTAL_ROUTES.SERVICES}/:serviceStatus/bundle/:bundleAttemptId/details`,
              element: <BundleDetailPage />,
            },
            {
              path: PORTAL_ROUTES.MY_ACCOUNT,
              element: <MyAccountPage testID="my-account-page" />,
            },
            {
              path: PORTAL_ROUTES.NOTIFICATIONS,
              element: <NotificationsPage />,
            },
            {
              path: PORTAL_ROUTES.MY_TASKS,
              element: <MyTasksPage />,
            },
            {
              path: PORTAL_ROUTES.SUPPORT_TICKETS,
              element: <SupportTicketsPage />,
            },
            {
              path: PORTAL_ROUTES.DASHBOARDS,
              element: <DashboardsPage />,
            },
            {
              path: PORTAL_ROUTES.SUPER_ADMIN_CATALOG_SYNC,
              element: <CatalogSyncPage />,
              loader: superAdminCatalogLoader,
            },
          ],
        },
        {
          path: PORTAL_ROUTES.MSA,
          element: <MSALayout />,
          loader: msaLoader,
          children: [
            {
              index: true,
              loader: () => redirect(PORTAL_ROUTES.MSA_INITIATE),
            },
            {
              path: PORTAL_ROUTES.MSA_INITIATE,
              element: <MSAInitiateLayout />,
            },
            {
              path: PORTAL_ROUTES.MSA_REVIEW,
              element: <MSAReviewLayout />,
            },
          ],
        },
        {
          path: PORTAL_ROUTES.COMPANY_EDIT,
          element: <CompanyEditLayout />,
          loader: companyEditLoaderWithRefresh,
          children: [
            {
              index: true,
            },
          ],
        },
        {
          path: PORTAL_ROUTES.NDA,
          element: <NDALayout />,
          loader: ndaLoaderWithRefresh,
        },
        {
          // Shared by Service Request and Bundle Request (TDF-1342 Phase 3)
          // — the two flows are UI-identical per design; ?serviceType=bundle
          // switches the loader/layout's data source. Absent means 'service'.
          path: `${PORTAL_ROUTES.SERVICES}/requested/:attemptId/review-contract`,
          element: <ServiceContractLayout />,
          loader: reviewContractLoader,
        },
        {
          path: `${PORTAL_ROUTES.SERVICES}/requested/:attemptId/review-proposal`,
          element: <ProposalReviewLayout />,
          loader: proposalReviewLoader,
        },
        {
          path: `${PORTAL_ROUTES.SERVICES}/requested/:attemptId/upload-po`,
          element: <POLayout />,
          loader: purchaseOrderLoader,
        },
        {
          path: PORTAL_ROUTES.KYB,
          element: <CompanyEditLayout isKyb={true} />,
          loader: kybLoaderWithRefresh,
        },
        {
          path: PORTAL_ROUTES.SOW,
          element: <SowPageLayout />,
          loader: sowLoader,
        },
        {
          path: PORTAL_ROUTES.BUNDLE_SOW,
          element: <BundleSowPage />,
          loader: bundleSowLoader,
        },
        {
          path: `${PORTAL_ROUTES.ACTIVITY_REQUEST}/:srAttemptId`,
          element: <ActivityRequestPageLayout />,
          loader: activityRequestLoader,
        },
        {
          path: '/business-landing',
          children: [
            {
              path: 'submission-details',
              element: <DocumentAttestationLayout />,
            },
            {
              path: 'kickoff-documentation',
              element: <KickoffDocumentationLayout />,
            },
            {
              path: 'proof-of-payment',
              element: <ProofOfPaymentPage />,
            },
          ],
        },

        {
          path: PORTAL_ROUTES.APP_INFO,
          element: <AppInfoPage />,
        },
        {
          path: '*',
          element: <NotFoundWrapper />,
        },
        {
          path: PORTAL_ROUTES.PROOF_OF_PAYMENT,
          element: <ProofOfPaymentPage />,
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);
